import React, { FC, useCallback, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/material';

import { DonutSeriesDTO, ProcessTestSummaryDTO } from '@/api/generated';
import LegendItem from '@/components/Charts/LegendItem';
import DropdownSelect from '@/components/fields/DropdownSelect';

import { format } from './utils';

interface TestsTabProps {
  chartSize: number;
  tests?: ProcessTestSummaryDTO[];
  testStats: { [key: string]: DonutSeriesDTO };
}

const TestsTab: FC<TestsTabProps> = ({ chartSize, tests, testStats }) => {
  const { t } = useTranslation(['processes', 'common']);
  const firstTestId = useMemo(() => tests?.[0]?.catalogItemId || '', [tests]);
  const [selectedTestId, setSelectedTestId] = useState<string>(firstTestId);

  const activeTest = useMemo(() => {
    const test = tests?.find(({ catalogItemId }) => catalogItemId === selectedTestId);
    if (!test || !testStats[selectedTestId]) return null;

    return {
      id: selectedTestId,
      test,
      stats: testStats[selectedTestId]
    };
  }, [selectedTestId, tests, testStats]);

  const testOptions = useMemo(
    () =>
      (tests || []).map(({ catalogItemId, name }) => ({
        value: catalogItemId!,
        label: name || t('layouts.processes.details.info.testPlaceholder')
      })),
    [tests, t]
  );

  const handleTestChange = useCallback((testId: string | string[]) => {
    setSelectedTestId(testId as string);
  }, []);

  const total = useMemo(() => activeTest?.stats?.total || 0, [activeTest]);

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          data: activeTest?.stats?.values || [],
          backgroundColor: activeTest?.stats?.colors?.filter((color): color is string => Boolean(color)) || [],
          borderWidth: 1,
          cutout: '70%'
        }
      ]
    }),
    [activeTest?.stats?.colors, activeTest?.stats?.values]
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <DropdownSelect
          options={testOptions}
          value={selectedTestId}
          onChange={handleTestChange}
          name="test-select"
          isMulti={false}
          isSearchable={false}
          sx={{
            '& .DropdownSelect-control': {
              border: 'none'
            }
          }}
        />
      </Box>

      <Box sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '50%' }}>
          <Doughnut
            data={chartData}
            options={{ cutout: '70%', responsive: false }}
            width={chartSize}
            height={chartSize}
          />
        </Box>
        <Stack
          sx={{
            marginInlineStart: 5,
            marginInlineEnd: 0,
            textAlign: 'start',
            whiteSpace: 'nowrap',
            gap: 1
          }}
        >
          {activeTest?.stats?.labels.map((label, index) => (
            <LegendItem
              key={index}
              title={label}
              color={activeTest?.stats?.colors?.[index] || ''}
              value={format(t, 'common:number.percent', (activeTest?.stats?.values?.[index] || 0) / total!)}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default TestsTab;
